import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faUserPlus, faTrash, faUserShield, faUserTimes, faSearch, faUserCog } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../../utils/apiConfig';
import '../../styles/admin.css';

function AccountUserManagement({ userId, accountId, accountName, onRefresh }) {
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  // Always set new users as account admins
  const [sortConfig, setSortConfig] = useState({
    key: 'email',
    direction: 'ascending'
  });

  useEffect(() => {
    if (accountId) {
      fetchAccountUsers();
    }
  }, [accountId]);

  const fetchAccountUsers = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(getApiUrl(`/api/account-admin/accounts/${accountId}/users`), {
        params: { userId }
      });
      setUsers(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching account users:', error);
      setError('Failed to load account users. Please try again later.');
      setIsLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await api.get(getApiUrl('/api/admin/users'), {
        params: { userId }
      });

      // Filter out users already in the account
      const accountUserIds = users.map(user => user.user_id);

      // Filter for regular Admin users only (exclude Super Admins)
      const availableUsers = response.data.filter(user => {
        // Check if user is not already in the account
        const notInAccount = !accountUserIds.includes(user.id);

        // Check if user has the Admin role (excluding Super Admin)
        const roleName = user.role_name || (user.role ? user.role.name : '');
        const isAdmin = roleName === 'Admin';

        return notInAccount && isAdmin;
      });

      // Make sure we have role information for each user
      const usersWithRoles = availableUsers.map(user => ({
        ...user,
        role_name: user.role_name || (user.role ? user.role.name : '')
      }));

      // Remove duplicate users by email address
      const uniqueUsers = [];
      const emailMap = new Map();

      usersWithRoles.forEach(user => {
        if (!emailMap.has(user.email)) {
          emailMap.set(user.email, true);
          uniqueUsers.push(user);
        }
      });

      setAllUsers(uniqueUsers);
    } catch (error) {
      console.error('Error fetching all users:', error);
      setError('Failed to load available users. Please try again later.');
    }
  };

  const handleAddUserClick = () => {
    fetchAllUsers();
    setSelectedUser('');
    setError(null);
    setSuccessMessage(null);
    setShowAddUserModal(true);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleAddUser = async (e) => {
    e.preventDefault();

    if (!selectedUser) {
      setError('Please select a user to add');
      return;
    }

    try {
      // Find the selected user's details for better error messages
      const selectedUserDetails = allUsers.find(user => user.id === selectedUser);
      const selectedUserName = selectedUserDetails ?
        (selectedUserDetails.display_name || selectedUserDetails.email) :
        'Selected user';

      console.log('Adding user to account:', {
        accountId,
        userId,
        targetUserId: selectedUser,
        userDetails: selectedUserDetails
      });

      // Prepare request data with explicit naming to avoid confusion
      const requestData = {
        userId: userId, // The admin performing the action (current user)
        targetUserId: selectedUser, // The user being added as admin
        isAccountAdmin: true // Always add as account admin
      };

      console.log('Sending request with data:', requestData);

      const response = await api.post(
        getApiUrl(`/api/account-admin/accounts/${accountId}/users`),
        requestData
      );

      console.log('Response from adding user:', response.data);

      // Verify the returned user is the one we selected
      if (response.data.user_id !== selectedUser) {
        console.error('API returned wrong user:', {
          expected: selectedUser,
          expectedName: selectedUserName,
          received: response.data.user_id,
          receivedName: response.data.display_name || response.data.email,
          fullResponse: response.data
        });

        // Instead of showing an error, let's try to recover by fetching the account users
        console.log('Attempting to recover by refreshing the account users list...');
        await fetchAccountUsers();

        // Check if the user we wanted to add is now in the list
        const userAdded = users.some(user => user.user_id === selectedUser);

        if (userAdded) {
          // User was actually added successfully despite the API returning the wrong user
          setSuccessMessage(`${selectedUserName} has been added as an admin to this account (recovered from API inconsistency).`);
          setError(null);
        } else {
          // User was not added, show the error
          setError(`System error: API returned incorrect user data. Expected to add ${selectedUserName} but got different results. Please try again or contact support.`);
          return;
        }
      }

      // Check if there's a message in the response (for existing users)
      if (response.data.message) {
        console.log('Message from API:', response.data.message);
        // Show a success message if the user was already an admin or was updated
        if (response.data.message.includes('already an admin')) {
          setSuccessMessage(`${selectedUserName} is already an admin of this account.`);
          setError(null); // Clear any existing errors
        } else if (response.data.message.includes('updated to an admin')) {
          setSuccessMessage(`${selectedUserName} has been updated to an admin of this account.`);
          setError(null); // Clear any existing errors
        }
      } else {
        // Regular add case
        setSuccessMessage(`${selectedUserName} has been added as an admin to this account.`);
        setError(null); // Clear any existing errors
      }

      // Close the modal first
      setShowAddUserModal(false);

      // Refresh the account users list to ensure we have the latest data
      await fetchAccountUsers();

      // Trigger refresh of all admin tables
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error adding user to account:', error);
      setSuccessMessage(null);
      setError('Failed to add user: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleToggleAdmin = async (user) => {
    try {
      const response = await api.put(
        getApiUrl(`/api/account-admin/accounts/${accountId}/users/${user.user_id}`),
        {
          userId: userId,
          isAccountAdmin: !user.is_account_admin
        }
      );

      // Update local state
      setUsers(users.map(u =>
        u.user_id === user.user_id ? { ...u, is_account_admin: response.data.is_account_admin } : u
      ));

      // Trigger refresh of all admin tables
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error toggling admin status:', error);
      setError('Failed to update user: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleRemoveUser = async (user) => {
    if (!window.confirm(`Are you sure you want to remove ${user.display_name || user.email} from this account?`)) {
      return;
    }

    try {
      await api.delete(
        getApiUrl(`/api/account-admin/accounts/${accountId}/users/${user.user_id}`),
        {
          params: { userId }
        }
      );

      // Update local state
      setUsers(users.filter(u => u.user_id !== user.user_id));

      // Trigger refresh of all admin tables
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error removing user from account:', error);
      setError('Failed to remove user: ' + (error.response?.data?.error || error.message));
    }
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) {
      return '';
    }
    return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle nested properties
      if (sortConfig.key.includes('.')) {
        const keys = sortConfig.key.split('.');
        aValue = keys.reduce((obj, key) => obj && obj[key], a);
        bValue = keys.reduce((obj, key) => obj && obj[key], b);
      }

      // Handle null or undefined values
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Convert to lowercase for string comparison
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      (user.email && user.email.toLowerCase().includes(searchTermLower)) ||
      (user.display_name && user.display_name.toLowerCase().includes(searchTermLower)) ||
      (user.role_name && user.role_name.toLowerCase().includes(searchTermLower))
    );
  });

  // Apply sorting to filtered users
  const sortedUsers = getSortedData(filteredUsers);

  // Filter available users based on search term
  const filteredAvailableUsers = allUsers.filter(user => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      (user.email && user.email.toLowerCase().includes(searchTermLower)) ||
      (user.display_name && user.display_name.toLowerCase().includes(searchTermLower)) ||
      (user.role_name && user.role_name.toLowerCase().includes(searchTermLower))
    );
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          {/* <div className="loading-spinner"></div> */}
          <p>Loading account users...</p>
        </div>
      </div>
    );
  }

  if (error && !showAddUserModal) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchAccountUsers}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <h2 className="admin-card-title">
          <FontAwesomeIcon icon={faUserCog} className="icon" />
          {accountName} - Account Admins
        </h2>
        <button
          className="btn btn-primary"
          onClick={handleAddUserClick}
        >
          <FontAwesomeIcon icon={faUserPlus} /> Add Admin
        </button>
      </div>

      {error && (
        <div className="alert alert-danger">{error}</div>
      )}

      {successMessage && (
        <div className="alert alert-success">{successMessage}</div>
      )}

      <div className="search-bar">
        <input
          type="text"
          className="search-input"
          placeholder="Search by email, name, or role..."
          value={searchTerm}
          onChange={handleSearch}
        />
        <button className="search-button">
          <FontAwesomeIcon icon={faSearch} />
        </button>
      </div>

      <div className="info-message">
        <p>Account admins can manage users and settings for this account.</p>
      </div>

      {/* Add Admin Modal */}
      {showAddUserModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>
                <FontAwesomeIcon icon={faUserShield} className="mr-2" />
                Add Admin User to {accountName}
              </h3>
              <button className="close-button" onClick={() => setShowAddUserModal(false)}>
                <FontAwesomeIcon icon={faUserTimes} />
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleAddUser}>
                <div className="form-group">
                  <label>Select User</label>
                  <select
                    className="form-control user-select-dropdown"
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    required
                  >
                    <option value="">-- Select an admin user --</option>
                    {filteredAvailableUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.display_name || 'Unnamed User'} - {user.email} {user.role_name ? `(${user.role_name})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <p className="text-info">
                  <FontAwesomeIcon icon={faUserShield} className="mr-2" />
                  Only users with the Admin role are shown. This user will be added as an account admin and will be able to manage users and settings for this account.
                </p>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAddUserModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Add Admin User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {users.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faUserShield} className="empty-state-icon" />
          <p className="empty-state-text">No account admins yet</p>
          <p className="empty-state-subtext">
            Add admins to help manage this account's users and settings.
          </p>
          <button
            className="btn btn-primary"
            onClick={handleAddUserClick}
          >
            <FontAwesomeIcon icon={faUserPlus} /> Add First Admin
          </button>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr>
                <th onClick={() => requestSort('display_name')} className="sortable-header">
                  Name{getSortDirectionIndicator('display_name')}
                </th>
                <th onClick={() => requestSort('email')} className="sortable-header">
                  Email{getSortDirectionIndicator('email')}
                </th>
                <th onClick={() => requestSort('role_name')} className="sortable-header">
                  Role{getSortDirectionIndicator('role_name')}
                </th>
                <th onClick={() => requestSort('added_by_name')} className="sortable-header" title="The user who added this admin to the account">
                  Added By{getSortDirectionIndicator('added_by_name')}
                </th>
                <th onClick={() => requestSort('created_at')} className="sortable-header">
                  Added On{getSortDirectionIndicator('created_at')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers
                // Filter to only show account admins
                .filter(user => user.is_account_admin)
                .map(user => (
                <tr key={user.id}>
                  <td>
                    <span>{user.display_name || '-'}</span>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`badge badge-${(user.role_name === 'Admin' || user.role_name === 'Super Admin') ? 'primary' : 'success'}`}>
                      {user.role_name || '-'}
                    </span>
                  </td>
                  <td>{user.added_by_name || '-'}</td>
                  <td>{formatDate(user.created_at)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleRemoveUser(user)}
                        title="Remove Admin"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AccountUserManagement;
